/**
 * =============================================================================
 * PATIENT JOURNEY - ACTIVE STEP BODY
 * =============================================================================
 *
 * Renders whatever the current node asks for:
 *
 *   acknowledgment -> report + prescription downloads and a confirm checkbox
 *   outcome        -> a divided list of outcome rows, with the date and time
 *                     field opening under the selected row when it needs one
 *   form           -> the referral to another facility details
 *
 * Plus the single action button that commits the step.
 * =============================================================================
 */

import React from "react";
import {
    Box,
    Typography,
    Checkbox,
    Button,
    TextField,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import prescriptionIcon from "../../../assets/icons/patient_prescription.svg";

import {
    NODE_TYPE,
    TIMESTAMP_LABEL,
    getOptionFields,
    isTimestampRequired,
    optionEndsCase,
} from "./journeyConfig";
import {
    FONT,
    JOURNEY_COLORS,
    endCaseChipSx,
    fieldLabelSx,
    helperTextSx,
    inputSx,
    primaryButtonSx,
    stepDescriptionSx,
} from "./journeyStyles";

/* -----------------------------------------------------------------------------
   INLINE ERROR
----------------------------------------------------------------------------- */

const InlineError = ({ message }) => {
    if (!message) {
        return null;
    }

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                marginTop: "8px",
            }}
        >
            <ErrorOutlineIcon
                sx={{
                    fontSize: "14px",
                    color: JOURNEY_COLORS.danger,
                }}
            />

            <Typography
                sx={{
                    ...helperTextSx,
                    color: JOURNEY_COLORS.danger,
                }}
            >
                {message}
            </Typography>
        </Box>
    );
};

/* -----------------------------------------------------------------------------
   DOCUMENT ROW
----------------------------------------------------------------------------- */

const DocumentRow = ({ file, onDownload }) => {
    const isPrescription = file.kind === "prescription";

    const isDisabled = !file.url;

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: "54px",
                borderRadius: "7px",
                backgroundColor: JOURNEY_COLORS.track,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                padding: { xs: "8px 10px", sm: "8px 14px" },
                marginBottom: "6px",
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: "9px", sm: "12px" },
                    minWidth: 0,
                }}
            >
                {isPrescription ? (
                    <Box
                        component="img"
                        src={prescriptionIcon}
                        alt=""
                        sx={{
                            width: "21px",
                            height: "21px",
                            display: "block",
                            flexShrink: 0,
                        }}
                    />
                ) : (
                    <DescriptionOutlinedIcon
                        sx={{
                            fontSize: "21px",
                            color: JOURNEY_COLORS.accent,
                            flexShrink: 0,
                        }}
                    />
                )}

                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontSize: "12px",
                            lineHeight: "16px",
                            fontWeight: 600,
                            color: JOURNEY_COLORS.text,
                        }}
                    >
                        {file.name}
                    </Typography>

                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontSize: "10px",
                            lineHeight: "14px",
                            color: JOURNEY_COLORS.textMuted,
                        }}
                    >
                        {file.size || "--"}
                    </Typography>
                </Box>
            </Box>

            <DownloadOutlinedIcon
                onClick={() => {
                    if (!isDisabled && onDownload) {
                        onDownload(file);
                    }
                }}
                sx={{
                    fontSize: "20px",
                    flexShrink: 0,
                    color: isDisabled
                        ? JOURNEY_COLORS.textMuted
                        : JOURNEY_COLORS.accent,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled ? 0.5 : 1,
                }}
            />
        </Box>
    );
};

/* -----------------------------------------------------------------------------
   RADIO
----------------------------------------------------------------------------- */

const OutcomeRadio = ({ isSelected }) => (
    <Box
        sx={{
            width: "18px",
            height: "18px",
            minWidth: "18px",
            borderRadius: "50%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border:
                (isSelected ? "2px solid " : "1px solid ") +
                (isSelected ? JOURNEY_COLORS.accent : JOURNEY_COLORS.line),
            transition: "border-color 0.15s ease",
        }}
    >
        {isSelected && (
            <Box
                sx={{
                    width: "9px",
                    height: "9px",
                    borderRadius: "50%",
                    backgroundColor: JOURNEY_COLORS.accent,
                }}
            />
        )}
    </Box>
);

/* -----------------------------------------------------------------------------
   SEGMENTED CONTROL

   The pill toggle used for short either/or answers such as the exit type.
----------------------------------------------------------------------------- */

const SegmentedControl = ({ field, value, onChange }) => (
    <Box
        sx={{
            display: { xs: "flex", sm: "inline-flex" },
            width: { xs: "100%", sm: "auto" },
            alignItems: "center",
            padding: "4px",
            borderRadius: "64px",
            backgroundColor: JOURNEY_COLORS.track,
            border: "1px solid " + JOURNEY_COLORS.border,
            boxSizing: "border-box",
        }}
    >
        {(field.options || []).map((option) => {
            const isSelected = value === option.value;

            return (
                <Box
                    key={option.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => onChange(field.name, option.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onChange(field.name, option.value);
                        }
                    }}
                    sx={{
                        flex: { xs: 1, sm: "0 0 auto" },
                        minWidth: { xs: 0, sm: "104px" },
                        height: "34px",
                        paddingX: { xs: "10px", sm: "18px" },
                        borderRadius: "64px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        userSelect: "none",
                        outline: "none",
                        backgroundColor: isSelected
                            ? JOURNEY_COLORS.accent
                            : "transparent",
                        transition: "background-color 0.15s ease",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: FONT,
                            fontSize: "12px",
                            lineHeight: "16px",
                            fontWeight: isSelected ? 600 : 500,
                            color: isSelected
                                ? "#FFFFFF"
                                : JOURNEY_COLORS.text,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {option.label}
                    </Typography>
                </Box>
            );
        })}
    </Box>
);

/* -----------------------------------------------------------------------------
   OUTCOME ROW
----------------------------------------------------------------------------- */

const OutcomeRow = ({
    option,
    isSelected,
    onSelect,
    children,
}) => (
    <Box
        sx={{
            borderBottom: "1px solid " + JOURNEY_COLORS.border,
            paddingBottom: isSelected && children ? "14px" : 0,
        }}
    >
        <Box
            role="radio"
            aria-checked={isSelected}
            tabIndex={0}
            onClick={() => onSelect(option.value)}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(option.value);
                }
            }}
            sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                columnGap: "12px",
                rowGap: "4px",
                paddingY: "6px",
                minHeight: "47px",
                cursor: "pointer",
                userSelect: "none",
                outline: "none",
                "&:focus-visible .journey-outcome-label": {
                    color: JOURNEY_COLORS.accent,
                },
            }}
        >
            <OutcomeRadio isSelected={isSelected} />

            <Typography
                className="journey-outcome-label"
                sx={{
                    fontFamily: FONT,
                    fontSize: "12px",
                    lineHeight: "17px",
                    fontWeight: 500,
                    color: JOURNEY_COLORS.text,
                    minWidth: 0,
                    overflowWrap: "anywhere",
                }}
            >
                {option.label}
            </Typography>

            {optionEndsCase(option) && (
                <Box sx={endCaseChipSx}>End Case</Box>
            )}
        </Box>

        {/* The date and time field opens under its own row. */}
        {isSelected && children}
    </Box>
);

/* -----------------------------------------------------------------------------
   BODY
----------------------------------------------------------------------------- */

const JourneyStepBody = ({
    node,
    draft,
    errors = {},
    willCloseCase = false,
    canSubmit = false,
    pendingRequirement = null,
    isSaving = false,
    showDescription = true,
    documents = [],
    onSelectOption,
    onTimestampChange,
    onFieldChange,
    onAcknowledgedChange,
    onDocumentDownload,
    onSubmit,
}) => {
    if (!node) {
        return null;
    }

    const isAcknowledgment = node.type === NODE_TYPE.ACKNOWLEDGMENT;
    const isForm = node.type === NODE_TYPE.FORM;
    const isOutcome = node.type === NODE_TYPE.OUTCOME;

    const options = node.options || [];

    const actionLabel = willCloseCase
        ? node.terminalActionLabel || "Close Case"
        : node.actionLabel || "Save & Continue";

    /* ---------------------------------------------------------------------
       FIELD - text, dropdown or pill toggle, with its label
    --------------------------------------------------------------------- */

    const renderField = (field) => (
        <Box key={field.name}>
            <Typography sx={fieldLabelSx}>
                {field.label}
                {field.required && (
                    <span style={{ color: JOURNEY_COLORS.danger }}>*</span>
                )}
            </Typography>

            {field.type === "segmented" ? (
                <SegmentedControl
                    field={field}
                    value={draft.values?.[field.name] || ""}
                    onChange={onFieldChange}
                />
            ) : (
                <TextField
                    fullWidth
                    size="small"
                    select={field.type === "select"}
                    value={draft.values?.[field.name] || ""}
                    onChange={(event) =>
                        onFieldChange(field.name, event.target.value)
                    }
                    placeholder={field.placeholder}
                    error={Boolean(errors[field.name])}
                    helperText={errors[field.name] || ""}
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (value) =>
                            value
                                ? (field.options || []).find(
                                      (item) => item.value === value
                                  )?.label || value
                                : field.placeholder,
                    }}
                    sx={inputSx}
                >
                    {(field.options || []).map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                                fontFamily: FONT,
                                fontSize: "12px",
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            )}

            {field.type === "segmented" && (
                <InlineError message={errors[field.name]} />
            )}
        </Box>
    );

    /* ---------------------------------------------------------------------
       SUB QUESTIONS - opened under the selected outcome row
    --------------------------------------------------------------------- */

    const renderOptionDetails = (option) => {
        const fields = getOptionFields(option);

        if (!option.requiresTimestamp && fields.length === 0) {
            return null;
        }

        return (
            <Box
                sx={{
                    paddingLeft: { xs: "8px", sm: "30px" },
                    maxWidth: { xs: "100%", sm: "440px" },
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                }}
            >
                {option.requiresTimestamp && (
                    <Box>
                        <Typography sx={fieldLabelSx}>
                            {node.timestampLabel || TIMESTAMP_LABEL}

                            {/* The date and time is optional unless a node
                                opts in, so no asterisk by default. */}
                            {isTimestampRequired(node, option) && (
                                <span
                                    style={{ color: JOURNEY_COLORS.danger }}
                                >
                                    *
                                </span>
                            )}
                        </Typography>

                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                value={draft.timestamp || null}
                                onChange={onTimestampChange}
                                format="dd/MM/yyyy hh:mm a"
                                disableFuture
                                enableAccessibleFieldDOMStructure={false}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        placeholder:
                                            "select date and time",
                                        error: Boolean(errors.timestamp),
                                        helperText: errors.timestamp || "",
                                        sx: inputSx,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                )}

                {fields.map(renderField)}
            </Box>
        );
    };

    return (
        <Box sx={{ width: "100%" }}>
            {/* ============ DESCRIPTION ============ */}

            {showDescription && node.description && (
                <Typography
                    sx={{
                        ...stepDescriptionSx,
                        marginTop: "12px",
                        marginBottom: "14px",
                    }}
                >
                    {node.description}
                </Typography>
            )}

            {/* ============ ACKNOWLEDGMENT ============ */}

            {isAcknowledgment && (
                <>
                    {documents.map((file) => (
                        <DocumentRow
                            key={file.id}
                            file={file}
                            onDownload={onDocumentDownload}
                        />
                    ))}

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            marginTop: "12px",
                        }}
                    >
                        <Checkbox
                            checked={Boolean(draft.acknowledged)}
                            onChange={(event) =>
                                onAcknowledgedChange(event.target.checked)
                            }
                            sx={{
                                padding: 0,
                                marginRight: "8px",
                                color: JOURNEY_COLORS.line,
                                "&.Mui-checked": {
                                    color: JOURNEY_COLORS.accent,
                                },
                                "& .MuiSvgIcon-root": {
                                    fontSize: "20px",
                                },
                            }}
                        />

                        <Typography
                            onClick={() =>
                                onAcknowledgedChange(!draft.acknowledged)
                            }
                            sx={{
                                fontFamily: FONT,
                                fontSize: "11px",
                                lineHeight: "20px",
                                color: JOURNEY_COLORS.textMuted,
                                cursor: "pointer",
                                userSelect: "none",
                            }}
                        >
                            {node.confirmLabel}
                        </Typography>
                    </Box>

                    <InlineError message={errors.acknowledged} />
                </>
            )}

            {/* ============ OUTCOME ROWS ============ */}

            {isOutcome && (
                <Box
                    role="radiogroup"
                    aria-label={node.title}
                    sx={{ marginTop: "4px" }}
                >
                    {options.map((option) => (
                        <OutcomeRow
                            key={option.value}
                            option={option}
                            isSelected={draft.optionValue === option.value}
                            onSelect={onSelectOption}
                        >
                            {renderOptionDetails(option)}
                        </OutcomeRow>
                    ))}
                </Box>
            )}

            {/* ============ FORM FIELDS ============ */}

            {isForm && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                        },
                        gap: "14px",
                    }}
                >
                    {(node.fields || []).map(renderField)}
                </Box>
            )}

            {/* ============ ACTION ============ */}

            <Button
                fullWidth
                disableElevation
                disabled={!canSubmit || isSaving}
                onClick={onSubmit}
                startIcon={
                    isSaving ? (
                        <CircularProgress size={14} sx={{ color: "inherit" }} />
                    ) : null
                }
                sx={primaryButtonSx(canSubmit && !isSaving)}
            >
                {isSaving ? "Saving..." : actionLabel}
            </Button>

            {/* ============ WHAT IS STILL MISSING ============ */}

            {!canSubmit && pendingRequirement && (
                <Typography
                    sx={{
                        ...helperTextSx,
                        color: JOURNEY_COLORS.textMuted,
                        textAlign: "center",
                        marginTop: "8px",
                    }}
                >
                    {pendingRequirement}
                </Typography>
            )}
        </Box>
    );
};

export default JourneyStepBody;
